import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Zap, ShieldCheck, Eye, Brain, Radar } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/SectionHeading";
import { PulseMark } from "@/components/site/PulseMark";
import { alternatePath } from "@/lib/i18n";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";
import { quotePath, quoteSearch } from "@/lib/quote-links";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const t = {
  eyebrow: "Execution Engine",
  title: "PULSE: From idea to deployed Shopify change, without breaking your store.",
  intro:
    "Our proprietary execution layer. It's how we ship faster, validate every change, and keep your live store untouched until you approve.",
  microcopy: "PULSE is included in every Shugert engagement. It is not sold separately.",
  problemEyebrow: "The execution problem",
  problemTitle: "Most agencies take weeks to ship a change. They risk your live store. They forget what they learned last sprint.",
  problemBody: "We solved that internally, so you get the benefit.",
  benefitsEyebrow: "What PULSE does for you",
  benefitsTitle: "Five things every engagement gets: automatically.",
  benefits: [
    { icon: Zap, title: "Days, not weeks", body: "Changes ship in days, not weeks. The bottleneck is no longer execution." },
    { icon: ShieldCheck, title: "7 automated checks", body: "Every change is validated against 7 automated checks (Liquid syntax, mobile responsiveness, breaking changes, performance, accessibility, security, code review) before it reaches a staging URL." },
    { icon: Eye, title: "You approve everything", body: "You preview every change at a private staging URL. Your live store is never touched without your click." },
    { icon: Brain, title: "It learns your store", body: "PULSE remembers your store. Each engagement gets faster and more accurate over time." },
    { icon: Radar, title: "Background opportunity scans", body: "Weekly audits surface new performance, CRO, and revenue opportunities: without you asking." },
  ],
  whereEyebrow: "Where PULSE shows up",
  whereTitle: "Inside every layer of how we work with you.",
  where: [
    { tag: "Revenue Fix", body: "Prioritized fixes from your Diagnostic ship through PULSE, validated, previewed, deployed." },
    { tag: "AI Revenue Engine (monthly)", body: "Continuous testing, audits, and deploys all run on PULSE." },
    { tag: "Retainer", body: "Weekly improvements ship through PULSE so your store keeps moving without breaking." },
  ],
  closerTitle: "PULSE is not something you buy.",
  closerBody: "It's how we deliver. The way to access it is to start an engagement.",
  primary: "Start a Revenue Diagnostic",
  secondary: "See the full system",
} as const;

const tEs = {
  eyebrow: "Motor de Ejecución",
  title: "PULSE: De la idea al cambio Shopify desplegado, sin romper tu tienda.",
  intro:
    "Nuestra capa de ejecución propietaria. Así enviamos más rápido, validamos cada cambio y mantenemos tu tienda en vivo intacta hasta que tú apruebes.",
  microcopy: "PULSE está incluido en cada engagement de Shugert. No se vende por separado.",
  problemEyebrow: "El problema de ejecución",
  problemTitle: "La mayoría de agencias tarda semanas en enviar un cambio. Arriesgan tu tienda en vivo. Olvidan lo que aprendieron el sprint pasado.",
  problemBody: "Lo resolvimos internamente, para que tú te beneficies.",
  benefitsEyebrow: "Qué hace PULSE por ti",
  benefitsTitle: "Cinco cosas que recibe cada engagement: automáticamente.",
  benefits: [
    { icon: Zap, title: "Días, no semanas", body: "Los cambios se entregan en días, no semanas. El cuello de botella deja de ser la ejecución." },
    { icon: ShieldCheck, title: "7 chequeos automáticos", body: "Cada cambio se valida con 7 chequeos automáticos (sintaxis Liquid, responsive mobile, cambios disruptivos, performance, accesibilidad, seguridad, code review) antes de llegar a una URL de staging." },
    { icon: Eye, title: "Tú apruebas todo", body: "Previsualizas cada cambio en una URL privada de staging. Tu tienda en vivo nunca se toca sin tu clic." },
    { icon: Brain, title: "Aprende de tu tienda", body: "PULSE recuerda tu tienda. Cada engagement se vuelve más rápido y preciso con el tiempo." },
    { icon: Radar, title: "Auditorías en segundo plano", body: "Auditorías semanales surfacean nuevas oportunidades de performance, CRO y revenue: sin que las pidas." },
  ],
  whereEyebrow: "Dónde aparece PULSE",
  whereTitle: "Dentro de cada capa de cómo trabajamos contigo.",
  where: [
    { tag: "Revenue Fix", body: "Los fixes priorizados de tu Diagnóstico se envían a través de PULSE, validados, previsualizados, desplegados." },
    { tag: "AI Revenue Engine (mensual)", body: "El testing continuo, auditorías y deploys corren todos sobre PULSE." },
    { tag: "Retainer", body: "Las mejoras semanales se envían a través de PULSE para que tu tienda siga avanzando sin romperse." },
  ],
  closerTitle: "PULSE no es algo que compras.",
  closerBody: "Es cómo entregamos. La forma de acceder es empezar un engagement.",
  primary: "Empezar con un Revenue Diagnostic",
  secondary: "Ver el sistema completo",
} as const;

function PulseContent({ locale }: { locale: "en" | "es" }) {
  const c = locale === "es" ? tEs : t;
  const quoteHref = quotePath(locale);
  const quoteSrch = quoteSearch({ intent: "diagnostic", source: "/pulse" });
  const systemHref = locale === "es" ? "/es/analitica-ai" : "/analytics-ai";

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Base grid */}
        <div className="absolute inset-0 bg-grid-faint opacity-30" aria-hidden="true" />
        {/* Cyan radial blob, top-right */}
        <div
          className="pointer-events-none absolute -right-32 -top-24 h-[600px] w-[600px] rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, color-mix(in oklab, var(--pulse-accent-1) 25%, transparent), transparent 70%)",
          }}
          aria-hidden="true"
        />
        {/* Indigo→violet diagonal sweep, bottom-left */}
        <div
          className="pointer-events-none absolute -bottom-40 -left-32 h-[520px] w-[720px] blur-3xl"
          style={{
            background:
              "linear-gradient(115deg, color-mix(in oklab, var(--pulse-accent-2) 18%, transparent), color-mix(in oklab, var(--pulse-accent-3) 14%, transparent) 60%, transparent 85%)",
          }}
          aria-hidden="true"
        />
        {/* Top vignette to soften header overlap */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-24"
          style={{
            background:
              "linear-gradient(to bottom, var(--background), transparent)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-5xl px-6 py-24 lg:py-32">
          <Reveal>
            <div className="inline-flex items-center gap-3 rounded-full border border-border bg-background/80 px-4 py-1.5 backdrop-blur-sm">
              <PulseMark variant="lockup" size="md" animated />
            </div>
          </Reveal>
          <Reveal delay={60}>
            <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
              {c.eyebrow}
            </p>
          </Reveal>
          {/* Not wrapped in <Reveal>: the H1 is the LCP element for this
              route, and Reveal renders opacity:0 until client hydration +
              IntersectionObserver, which delays LCP. */}
          <h1 className="mt-8 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-foreground text-balance sm:text-5xl lg:text-6xl">
            {c.title}
          </h1>
          <Reveal delay={160}>
            <p className="mt-6 max-w-3xl text-pretty text-lg leading-relaxed text-muted-foreground sm:text-xl">
              {c.intro}
            </p>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-6 inline-flex rounded-full border border-border bg-card/50 px-4 py-2 text-xs font-medium text-muted-foreground">
              {c.microcopy}
            </p>
          </Reveal>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-card/30 py-24">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-muted-foreground">
              {c.problemEyebrow}
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">
              {c.problemTitle}
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {c.problemBody}
            </p>
          </Reveal>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <Reveal>
          <SectionHeading eyebrow={c.benefitsEyebrow} title={c.benefitsTitle} align="center" />
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-3xl border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
          {c.benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <Reveal key={b.title} delay={i * 60} className="bg-background p-8">
                <Icon className="h-5 w-5 text-foreground" aria-hidden="true" />
                <h3 className="mt-4 font-display text-lg font-semibold tracking-tight text-foreground">
                  {b.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{b.body}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* WHERE PULSE SHOWS UP */}
      <section className="bg-card/30 py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <SectionHeading eyebrow={c.whereEyebrow} title={c.whereTitle} align="center" />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {c.where.map((w, i) => (
              <Reveal key={w.tag} delay={i * 80}>
                <div className="rounded-2xl border border-border bg-background p-7">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    {w.tag}
                  </p>
                  <p className="mt-4 text-base leading-relaxed text-foreground">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSER / SOFT CTA */}
      <section className="mx-auto max-w-7xl px-6 py-32">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-border bg-card p-10 sm:p-16">
            <div
              className="absolute -right-32 -top-32 h-96 w-96 rounded-full blur-3xl"
              style={{
                background:
                  "radial-gradient(circle, color-mix(in oklab, var(--color-foreground) 22%, transparent), transparent 70%)",
              }}
              aria-hidden="true"
            />
            <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <h2 className="font-display text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl">
                  {c.closerTitle}
                </h2>
                <p className="mt-4 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg">
                  {c.closerBody}
                </p>
              </div>
              <div className="flex flex-col gap-3 lg:col-span-4 lg:items-end">
                <Link
                  to={quoteHref}
                  search={quoteSrch}
                  className="inline-flex items-center gap-2 rounded-full bg-foreground px-7 py-3.5 text-sm font-medium text-background transition-all hover:gap-3 hover:bg-foreground/90"
                >
                  {c.primary}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to={systemHref}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {c.secondary} →
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}

export const Route = createFileRoute("/pulse")({
  loader: async () => { await setEdgeHtmlCache(); },
  head: () =>
    buildHead({
      locale: "en",
      title: "PULSE: Shopify Execution Engine · Shugert",
      description:
        "PULSE is Shugert's proprietary execution layer. Validated Shopify changes, staging-first previews, zero risk to your live store. Included in every engagement.",
      path: "/pulse",
      altPath: alternatePath("/pulse", "es"),
      ogImage: "/og-pulse-v2.jpg",
      jsonLd: breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "PULSE", path: "/pulse" },
      ]),
    }),
  component: () => (
    <SiteShell>
      <PulseContent locale="en" />
    </SiteShell>
  ),
});

export { PulseContent };
